import { Runtime, pipe } from "fp";
import { QueryKey } from "react-query";
import { Company } from "~/Company";
import { ID, Reference } from "~/Data";
import { Remote } from "~/Remote";
import { Request } from "~/Request";

export type Assets = {
  readonly vehicles?: readonly string[];
  readonly trailers?: readonly string[];
};

export type Asset = string;

export namespace Response {
  export type Response = Runtime.ToType<typeof runtime>;

  export const runtime = () =>
    Runtime.strict({
      vehicles: Runtime.array(Runtime.string()),
      trailers: Runtime.array(Runtime.string()),
    });

  export const decode = ({
    vehicles,
    trailers,
  }: Response): Assets | undefined =>
    pipe(
      {
        vehicles: vehicles[0] ? vehicles : undefined,
        trailers: trailers[0] ? trailers : undefined,
      },
      (assets) => (assets.vehicles ?? assets.trailers ? assets : undefined)
    );
}

export namespace DOT {
  export const toEndpoint = ({ company, asset }: QueryParams) =>
    `/companies/${company.id}/assets?asset_id=${asset.id}`;

  export type QueryIdParams = {
    readonly asset: Reference.Reference;
  };

  export type QueryParams = QueryIdParams & {
    readonly company: Reference.Reference;
  };

  export const tag = () => "Asset DOTs" as const;
  export const id = ({ asset }: QueryIdParams): ID.ID => `${asset.id}`;
  export const key = ({ asset }: QueryIdParams): QueryKey => [
    tag(),
    id({ asset }),
  ];

  export const use = ({ asset }: QueryIdParams): Remote<AssetDOT> => {
    const company = Company.use().company;

    return pipe(Remote.Query.use(key({ asset }), request({ company, asset })));
  };

  export const request = ({
    company,
    asset,
  }: QueryParams): Request.Fn<AssetDOT> => {
    const endpoint = toEndpoint({ company, asset });
    return flow(
      Request.fromConfig({
        endpoint,
        headers: {
          "X-Fields": "{asset_id custom_name dot_number}",
        },
      }),
      Request.Expect.status(200),
      Request.Expect.json(Response.runtime()),
      Request.onSuccess(Response.decode())
    );
  };

  export type AssetDOT = {
    readonly assetID: string;
    readonly dotNumber: string;
    readonly customName?: string;
  };
  export namespace Response {
    export const runtime = () =>
      Runtime.array(
        Runtime.strict({
          dot_number: Runtime.strict({
            dot_number_id: Runtime.number(),
            dot_number: Runtime.number(),
            company_id: Runtime.number(),
            description: Runtime.nullable(Runtime.string()),
          }),
          asset_id: Runtime.number(),
          custom_name: Runtime.nullable(Runtime.string()),
        })
      );

    export const decode = () => (
      response: Runtime.ToType<typeof runtime>
    ): AssetDOT => ({
      assetID: `${response[0].asset_id}`,
      dotNumber: `${response[0].dot_number.dot_number}`,
      customName: response[0].custom_name ?? undefined,
    });
  }
}
